import { handleUnauthorized, getAuthToken } from './authApi';

function getDefaultApiRoot() {
  if (typeof window !== 'undefined') {
    const host = String(window.location.hostname || '').toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'https://api.andhrayatri.com/api';
    }
  }
  return 'https://api.andhrayatri.com/api';
}

function normalizeApiRoot(value) {
  const raw = String(value || getDefaultApiRoot()).trim();
  if (raw.startsWith('/')) {
    return raw.replace(/\/$/, '') || '/api';
  }
  const withoutTrailingSlash = raw.replace(/\/$/, '');
  if (/\/api(\/.*)?$/i.test(withoutTrailingSlash)) {
    return withoutTrailingSlash;
  }
  return `${withoutTrailingSlash}/api`;
}

export const API_ROOT = normalizeApiRoot(process.env.REACT_APP_API_BASE_URL);


const API_BASES = {
  customer: `${API_ROOT}/customer`,
  accountSettings: `${API_ROOT}/account-settings`,
  flight: `${API_ROOT}/flight`,
  bus: `${API_ROOT}/busapi`,
  hotel: `${API_ROOT}/hotel`,
  payment: `${API_ROOT}/payment/razorpay`,
  vendor: `${API_ROOT}/vendor`,
  product: `${API_ROOT}/product`,
  shopOrder: `${API_ROOT}/shop-order`,
  rider: `${API_ROOT}/rider`,
  rideOrder: `${API_ROOT}/ride-order`,
  banner: `${API_ROOT}/banner`,
  destination: `${API_ROOT}/destination`,
  outdoor: `${API_ROOT}/outdoor`,
};

function resolveRequestCredentials(url, requestedCredentials) {
  if (requestedCredentials) return requestedCredentials;
  if (typeof window === 'undefined') return 'same-origin';

  try {
    const requestUrl = new URL(url, window.location.origin);
    return requestUrl.origin === window.location.origin ? 'include' : 'omit';
  } catch {
    return 'same-origin';
  }
}

export function getApiError(error) {
  if (!error) return 'Something went wrong. Please try again.';
  if (typeof error === 'string') return error;
  if (error.message) return error.message;
  return 'Something went wrong. Please try again.';
}

function extractErrorMessage(payload, fallback) {
  if (!payload || typeof payload !== 'object') return fallback;
  if (typeof payload.message === 'string') return payload.message;
  if (typeof payload.error === 'string') return payload.error;
  if (typeof payload.detail === 'string') return payload.detail;
  if (payload.Error && typeof payload.Error === 'object' && typeof payload.Error.ErrorMessage === 'string') {
    return payload.Error.ErrorMessage || fallback;
  }

  const firstFieldError = Object.values(payload).find((value) => {
    if (Array.isArray(value)) return value.find((item) => typeof item === 'string');
    return typeof value === 'string';
  });

  if (Array.isArray(firstFieldError)) {
    return firstFieldError.find((item) => typeof item === 'string') || fallback;
  }

  return firstFieldError || fallback;
}

function assertSuccessfulSupplierResponse(payload) {
  if (!payload || typeof payload !== 'object') return;
  const resultRecord = Object.values(payload).find(
    (value) => value && typeof value === 'object' && !Array.isArray(value) && 'Error' in value
  );
  const nestedError = payload.Error || resultRecord?.Error;
  if (!nestedError || typeof nestedError !== 'object') return;

  const code = Number(nestedError.ErrorCode);
  const message = typeof nestedError.ErrorMessage === 'string' ? nestedError.ErrorMessage.trim() : '';
  if (Number.isFinite(code) && code !== 0) {
    throw new Error(message || `Supplier error ${code}.`);
  }
}

/* 
 * Cookie-Based Authentication Preparation
 *
 * The frontend is configured with credentials: 'include' 
 * to support future HttpOnly cookie-based session verification.
 * Demo mode continues using localStorage-based sessions.
 */
export async function apiRequest(url, options = {}) {
  let response;

  let token = '';
  if (!options.anonymous) {
    try {
      const customerSession = JSON.parse(localStorage.getItem('andhraYatriCustomerSession'));
      const vendorSession = JSON.parse(localStorage.getItem('andhraYatriVendorSession'));
      const adminSession = JSON.parse(localStorage.getItem('andhraYatriAdminSession'));

      const isEditingAdmin = typeof window !== 'undefined' &&
        (window.location.pathname.startsWith('/admin') || window.location.hash.startsWith('#/admin'));

      if (isEditingAdmin) {
        token = adminSession?.token || customerSession?.token || vendorSession?.token || getAuthToken() || '';
      } else {
        token = customerSession?.token || vendorSession?.token || adminSession?.token || getAuthToken() || '';
      }
    } catch {
      token = getAuthToken() || '';
    }
  }

  const headers = {
    ...options.headers,
  };

  if (token && !headers.Authorization && !headers.authorization) {
    headers.Authorization = `Bearer ${token}`;
  }

  const requestOptions = {
    ...options,
    headers,
    credentials: resolveRequestCredentials(url, options.credentials),
  };
  try {
    response = await fetch(url, requestOptions);
  } catch {
    throw new Error(`Unable to reach the Andhra Yatri API at ${API_ROOT}. Please check the deployed API URL and CORS/SSL settings.`);
  }

  const contentType = response.headers.get('content-type') || '';
  const payload = contentType.includes('application/json') ? await response.json() : await response.text();

  if (!response.ok) {
    const errMsg = extractErrorMessage(payload, `Request failed with status ${response.status}`);
    if (response.status === 401 && !url.includes('/login') && !url.includes('/auth/')) {
      handleUnauthorized();
    }
    throw new Error(errMsg);
  }

  assertSuccessfulSupplierResponse(payload);
  return payload;
}

export const outdoorApi = {
  getCategories(type = '', admin = false) {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    if (admin) params.set('admin', 'true');
    const query = params.toString();
    return apiRequest(`${API_BASES.outdoor}/categories${query ? `?${query}` : ''}`);
  },

  createCategory(payload) {
    return apiRequest(`${API_BASES.outdoor}/categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  updateCategory(categoryId, payload) {
    return apiRequest(`${API_BASES.outdoor}/categories/${encodeURIComponent(categoryId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  deleteCategory(categoryId, hard = false) {
    const query = hard ? '?hard=true' : '';
    return apiRequest(`${API_BASES.outdoor}/categories/${encodeURIComponent(categoryId)}${query}`, {
      method: 'DELETE',
    });
  },

  getItems({ type = '', categoryId = '', admin = false } = {}) {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    if (categoryId) params.set('categoryId', categoryId);
    if (admin) params.set('admin', 'true');
    const query = params.toString();
    return apiRequest(`${API_BASES.outdoor}/items${query ? `?${query}` : ''}`);
  },

  getItem(itemId) {
    return apiRequest(`${API_BASES.outdoor}/items/${encodeURIComponent(itemId)}`);
  },

  createItem(payload) {
    return apiRequest(`${API_BASES.outdoor}/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  updateItem(itemId, payload) {
    return apiRequest(`${API_BASES.outdoor}/items/${encodeURIComponent(itemId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  deleteItem(itemId, hard = false) {
    const query = hard ? '?hard=true' : '';
    return apiRequest(`${API_BASES.outdoor}/items/${encodeURIComponent(itemId)}${query}`, {
      method: 'DELETE',
    });
  },

  createBooking(payload) {
    return apiRequest(`${API_BASES.outdoor}/bookings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  getBooking(bookingId) {
    return apiRequest(`${API_BASES.outdoor}/bookings/${encodeURIComponent(bookingId)}`);
  },
};

export const customerApi = {
  getAllCustomers() {
    return apiRequest(`${API_BASES.customer}/all`, {
      method: 'GET',
      anonymous: true,
    });
  },

  checkMobile(mobile) {
    return apiRequest(`${API_BASES.customer}/check-mobile`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mobile }),
    });
  },

  sendMobileOtp(mobile, purpose = 'login') {
    return apiRequest(`${API_BASES.customer}/send-mobile-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mobile, purpose }),
    });
  },

  verifyMobileOtp(mobile, otp, purpose = 'login') {
    return apiRequest(`${API_BASES.customer}/verify-mobile-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mobile, otp, purpose }),
    });
  },

  loginCustomer({ email, mobile, password }) {
    const payload = { password };
    if (email) payload.email = email;
    if (mobile) payload.mobile = mobile;

    return apiRequest(`${API_BASES.customer}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  signupCustomer({ name, email, mobile, password }) {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('email', email);
    formData.append('mobile', mobile);
    formData.append('password', password);

    return apiRequest(`${API_BASES.customer}/add`, {
      method: 'POST',
      body: formData,
    });
  },

  registerCustomer({ name, email, mobile, password }) {
    return apiRequest(`${API_BASES.customer}/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, mobile, password }),
    });
  },

  updateCustomerProfile(customerId, payload) {
    return apiRequest(`${API_BASES.customer}/update/${customerId}/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },
};

export const bannerApi = {
  getAll(type = '') {
    const query = type ? `?type=${type}` : '';
    return apiRequest(`${API_BASES.banner}/all${query}`, {
      method: 'GET',
    });
  },

  add(payload) {
    return apiRequest(`${API_BASES.banner}/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  update(bannerId, payload) {
    return apiRequest(`${API_BASES.banner}/update/${bannerId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  delete(bannerId) {
    return apiRequest(`${API_BASES.banner}/delete/${bannerId}`, {
      method: 'DELETE',
    });
  },
};

const destinationPromiseCache = new Map();

export const destinationApi = {
  getAll() {
    return apiRequest(`${API_BASES.destination}/all`, {
      method: 'GET',
    });
  },

  getBySlug(slug) {
    const key = String(slug || '').toLowerCase().trim();
    if (!key) {
      const err = new Error('Invalid destination identifier.');
      err.code = 'DESTINATION_NOT_FOUND';
      err.status = 404;
      return Promise.reject(err);
    }

    if (destinationPromiseCache.has(key)) {
      return destinationPromiseCache.get(key);
    }

    const requestPromise = apiRequest(`${API_BASES.destination}/${encodeURIComponent(key)}`, {
      method: 'GET',
    })
      .then((data) => {
        if (!data || data.error) {
          destinationPromiseCache.delete(key);
          const err = new Error(data?.error || 'Destination not found');
          err.code = 'DESTINATION_NOT_FOUND';
          err.status = 404;
          throw err;
        }
        return data;
      })
      .catch((error) => {
        destinationPromiseCache.delete(key);
        if (!error.code) {
          error.code = String(error.message || '').includes('Unable to reach') ? 'NETWORK_ERROR' : 'SERVER_ERROR';
        }
        throw error;
      });

    destinationPromiseCache.set(key, requestPromise);
    return requestPromise;
  },

  getById(destinationId) {
    return this.getBySlug(destinationId);
  },

  add(payload) {
    return apiRequest(`${API_BASES.destination}/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  update(destinationId, payload) {
    return apiRequest(`${API_BASES.destination}/update/${encodeURIComponent(destinationId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  delete(destinationId) {
    return apiRequest(`${API_BASES.destination}/delete/${encodeURIComponent(destinationId)}`, {
      method: 'DELETE',
    });
  },
};

export const tourGuideApi = {
  getAll(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '') {
        params.append(key, val);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest(`${API_ROOT}/tourguided/${query}`, {
      method: 'GET',
    });
  },

  getById(id) {
    return apiRequest(`${API_ROOT}/tourguided/${id}`, {
      method: 'GET',
    });
  },

  add(payload) {
    return apiRequest(`${API_ROOT}/tourguided/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  update(id, payload) {
    return apiRequest(`${API_ROOT}/tourguided/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  delete(id) {
    return apiRequest(`${API_ROOT}/tourguided/${id}`, {
      method: 'DELETE',
    });
  },
};

export const vendorApi = {
  loginVendor({ email, password }) {
    return apiRequest(`${API_BASES.vendor}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  },

  signupVendor(payload) {
    return apiRequest(`${API_BASES.vendor}/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  loginAdmin({ email, password }) {
    return apiRequest(`${API_BASES.vendor}/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  },

  getAdminVendors(status = 'pending', token = '') {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    return apiRequest(`${API_BASES.vendor}/admin/vendors?${params.toString()}`, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },

  updateVendorApproval(vendorId, approved, token = '', reason = '') {
    return apiRequest(`${API_BASES.vendor}/admin/vendors/${vendorId}/approval`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ approved, reason }),
    });
  },

  updateVendor(vendorId, payload, token = '') {
    return apiRequest(`${API_BASES.vendor}/update/${vendorId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });
  },

  getVendorProfile(vendorId, token = '') {
    return apiRequest(`${API_BASES.vendor}/${vendorId}`, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },

  getAllVendors() {
    return apiRequest(`${API_BASES.vendor}/all`, {
      method: 'GET',
    });
  },
};

export const productApi = {
  getByCategory(category) {
    return apiRequest(`${API_BASES.product}/by-category/${encodeURIComponent(category)}`, {
      method: 'GET',
    });
  },

  getByVendor(vendorId) {
    return apiRequest(`${API_BASES.product}/by-vendor/${vendorId}`, {
      method: 'GET',
    });
  },

  addProduct(payload) {
    return apiRequest(`${API_BASES.product}/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  updateProduct(productId, payload) {
    return apiRequest(`${API_BASES.product}/update/${productId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  deleteProduct(productId) {
    return apiRequest(`${API_BASES.product}/delete/${productId}`, {
      method: 'DELETE',
    });
  },
};

export const spiritualApi = {
  getAll(params = {}) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '') {
        searchParams.set(key, val);
      }
    });
    return apiRequest(`${API_BASES.product}/temples?${searchParams.toString()}`, {
      method: 'GET',
    });
  },

  getById(id) {
    return apiRequest(`${API_BASES.product}/temples/${id}`, {
      method: 'GET',
    });
  },

  create(payload) {
    return apiRequest(`${API_BASES.product}/temples`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  update(id, payload) {
    return apiRequest(`${API_BASES.product}/temples/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  delete(id) {
    return apiRequest(`${API_BASES.product}/temples/${id}`, {
      method: 'DELETE',
    });
  },
};

export const shopOrderApi = {
  getVendorOrders(vendorId) {
    return apiRequest(`${API_BASES.shopOrder}/vendor/${vendorId}`, {
      method: 'GET',
    });
  },
  updateOrderStatus(orderId, nextStatus, payload = {}) {
    return apiRequest(`${API_BASES.shopOrder}/${orderId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        status: nextStatus,
        ...payload,
      }),
    });
  },
};

export const rideOrderApi = {
  getAll(status = 'Placed') {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    return apiRequest(`${API_BASES.rideOrder}/all?${params.toString()}`, {
      method: 'GET',
    });
  },

  getRide(rideId) {
    return apiRequest(`${API_BASES.rideOrder}/${rideId}`, {
      method: 'GET',
    });
  },

  updateStatus(rideId, payload) {
    return apiRequest(`${API_BASES.rideOrder}/${rideId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  broadcastNotification(payload) {
    return apiRequest(`${API_BASES.rideOrder}/broadcast`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },
};

export const riderApi = {
  getAdminList(status = 'approved', token = '') {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    return apiRequest(`${API_BASES.rider}/admin/list?${params.toString()}`, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },

  getLocations() {
    return apiRequest(`${API_BASES.rider}/locations`, {
      method: 'GET',
    });
  },

  updateApproval(riderId, approved, token = '') {
    return apiRequest(`${API_BASES.rider}/admin/${riderId}/approval`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ approved }),
    });
  },
};

export const dashboardApi = {
  getCustomerDashboard(customerId) {
    return apiRequest(`${API_BASES.customer}/dashboard/${customerId}`, {
      method: 'GET',
    });
  },

  getCustomerBookings(customerId, params = {}) {
    const query = new URLSearchParams({ customerId, ...params }).toString();
    return apiRequest(`${API_BASES.customer}/bookings?${query}`, {
      method: 'GET',
    });
  },

  getCustomerTotals(customerId) {
    if (!customerId) return Promise.resolve(null);
    return apiRequest(`${API_BASES.customer}/totals/${encodeURIComponent(customerId)}`, {
      method: 'GET',
    }).catch(() => {
      const query = new URLSearchParams({ customerId }).toString();
      return apiRequest(`${API_BASES.customer}/totals?${query}`, {
        method: 'GET',
      });
    });
  },

  getCustomerBookingDetail(customerId, bookingId, category) {
    const query = new URLSearchParams({ customerId, bookingId, category }).toString();
    return apiRequest(`${API_BASES.customer}/bookings/detail?${query}`, {
      method: 'GET',
    });
  },

  getCustomerActivity(customerId) {
    return apiRequest(`${API_BASES.customer}/activity/${customerId}`, {
      method: 'GET',
    });
  },

  saveCustomerActivity(customerId, payload) {
    const type = payload.activityType || payload.type || payload.module || 'bus';
    const refId = payload.referenceId || payload.id || payload.bookingId || `AY-REF-${Date.now()}`;
    const activityPayload = {
      customerId: customerId || payload.customerId || 'cust_123',
      activityType: type,
      title: payload.title || 'Booking',
      amount: Number(payload.amount || payload.totalFare || 0),
      status: payload.status || 'Confirmed',
      created_at: payload.created_at || new Date().toISOString(),
      travelDate: payload.travelDate || payload.journeyDate || payload.checkInDate || new Date().toISOString().slice(0, 10),
      referenceCollection: payload.referenceCollection || `${type}-booking`,
      referenceId: refId,
      providerReferenceId: payload.providerReferenceId || payload.pnr || payload.confirmationId || refId,
      payload: payload.payload || payload,
    };
    return apiRequest(`${API_BASES.customer}/activity/${customerId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(activityPayload),
    }).catch(() => {
      return apiRequest(`${API_BASES.customer}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(activityPayload),
      });
    });
  },

  deleteCustomerActivity(customerId, activityId) {
    return apiRequest(`${API_BASES.customer}/activity/${customerId}/${encodeURIComponent(activityId)}`, {
      method: 'DELETE',
    });
  },

  getSavedPlaces(customerId) {
    return apiRequest(`${API_BASES.customer}/saved-places/${customerId}`, {
      method: 'GET',
    });
  },

  saveSavedPlace(customerId, payload) {
    return apiRequest(`${API_BASES.customer}/saved-places/${customerId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  updateSavedPlace(customerId, placeId, payload) {
    return apiRequest(`${API_BASES.customer}/saved-places/${customerId}/${encodeURIComponent(placeId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  deleteSavedPlace(customerId, placeId) {
    return apiRequest(`${API_BASES.customer}/saved-places/${customerId}/${encodeURIComponent(placeId)}`, {
      method: 'DELETE',
    });
  },

  getCustomerSettings(customerId) {
    return apiRequest(`${API_BASES.accountSettings}/customer/${customerId}`, {
      method: 'GET',
    });
  },

  patchCustomerSettings(customerId, payload) {
    return apiRequest(`${API_BASES.accountSettings}/customer/${customerId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  putCustomerSettings(customerId, payload) {
    return apiRequest(`${API_BASES.accountSettings}/customer/${customerId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },
};

export const travelApi = {
  getAirports(query = '') {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    params.set('limit', '20');
    return apiRequest(`${API_BASES.flight}/airports?${params.toString()}`, {
      method: 'GET',
    });
  },

  searchFlights(payload) {
    return apiRequest(`${API_BASES.flight}/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  getFlightFareQuote(payload) {
    return apiRequest(`${API_BASES.flight}/fare-quote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  getFlightSeatMap(payload) {
    return apiRequest(`${API_BASES.flight}/seat-map`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  bookFlight(payload) {
    return apiRequest(`${API_BASES.flight}/ticket`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  sendFlightTicketEmail(payload) {
    return apiRequest(`${API_BASES.flight}/ticket-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  getBusCities(query = '') {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    params.set('limit', '20');
    return apiRequest(`${API_BASES.bus}/cities?${params.toString()}`, {
      method: 'GET',
    });
  },

  searchBuses(payload) {
    return apiRequest(`${API_BASES.bus}/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  getBusSeatLayout(payload) {
    return apiRequest(`${API_BASES.bus}/seatlayout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {
      return apiRequest(`${API_BASES.bus}/seat-layout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    });
  },

  getBusBoardingPoints(payload) {
    return apiRequest(`${API_BASES.bus}/boardingpoints`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {
      return apiRequest(`${API_BASES.bus}/boarding-points`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    });
  },

  blockBus(payload) {
    return apiRequest(`${API_BASES.bus}/block`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  bookBus(payload) {
    return apiRequest(`${API_BASES.bus}/book`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  getBusTicketInvoice(payload) {
    return apiRequest(`${API_BASES.bus}/invoice`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {
      return apiRequest(`${API_BASES.bus}/ticket-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    });
  },

  sendBusTicketEmail(payload) {
    return apiRequest(`${API_BASES.bus}/ticket-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  searchHotels(payload) {
    return apiRequest(`${API_BASES.hotel}/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  getHotelCities(query) {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    params.set('limit', '12');
    return apiRequest(`${API_BASES.hotel}/cities?${params.toString()}`);
  },

  getHotelResources(requestType = 'Special') {
    return apiRequest(`${API_BASES.hotel}/resources`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ RequestType: requestType }),
    });
  },

  getHotelInfo(payload) {
    return apiRequest(`${API_BASES.hotel}/info`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  getHotelRooms(payload) {
    return apiRequest(`${API_BASES.hotel}/roomrate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {
      return apiRequest(`${API_BASES.hotel}/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    });
  },

  blockHotelRoom(payload) {
    return apiRequest(`${API_BASES.hotel}/block`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {
      return apiRequest(`${API_BASES.hotel}/block-room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    });
  },

  bookHotel(payload) {
    return apiRequest(`${API_BASES.hotel}/book`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  cancelHotelBooking(payload) {
    return apiRequest(`${API_BASES.hotel}/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  getHotelBookingStatus(params) {
    const searchParams = new URLSearchParams(params);
    return apiRequest(`${API_BASES.hotel}/booking-status?${searchParams.toString()}`);
  },

  getHotelShareToken(payload) {
    return apiRequest(`${API_BASES.hotel}/share-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  retryHotelBooking(payload) {
    return apiRequest(`${API_BASES.hotel}/retry-booking`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  createRazorpayOrder(payload) {
    return apiRequest(`${API_BASES.payment}/create-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },

  verifyRazorpayPayment(payload) {
    return apiRequest(`${API_BASES.payment}/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  },
};

// ── Official v2.0 Andhra Yatri Service API Re-exports ──
export {
  homeApi,
  ridesApi,
  templesApi,
  tourGuidesApi,
  newsApi,
  busBookingApi,
  flightBookingApi,
  hotelBookingApi,
  foodApi,
  offersApi,
  notificationsApi,
} from './andhraServicesApi';